import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ESTADOS_CICLO, TIPOS_FIDEICOMISO, labelTipo } from "../lib/catalogs";
import TabResumen from "../components/fideicomiso/TabResumen";
import TabPartes from "../components/fideicomiso/TabPartes";
import TabDocumental from "../components/fideicomiso/TabDocumental";
import TabBitacoraFid from "../components/fideicomiso/TabBitacoraFid";

export default function FideicomisoDetail() {
    const { id } = useParams();
    const [fid, setFid] = useState(null);
    const [completitud, setCompletitud] = useState(null);

    const loadFid = async () => {
        try {
            const { data } = await api.get(`/fideicomisos/${id}`);
            setFid(data);
        } catch (err) {
            toast.error("No se pudo cargar", { description: err?.response?.data?.detail });
        }
    };
    const loadCompletitud = async () => {
        try {
            const { data } = await api.get(`/fideicomisos/${id}/completitud`);
            setCompletitud(data);
        } catch {
            /* silent */
        }
    };

    useEffect(() => {
        loadFid();
        loadCompletitud();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (!fid) return <div className="p-10 text-muted-foreground text-sm">Cargando…</div>;

    const est = ESTADOS_CICLO.find((e) => e.value === fid.estado_ciclo);

    return (
        <div className="max-w-[1600px]" data-testid="fideicomiso-detail-page">
            {/* Header fijo con datos */}
            <div className="sticky top-0 z-20 bg-background border-b border-border">
                <div className="px-8 lg:px-10 py-6">
                    <Link
                        to="/fideicomisos"
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3"
                    >
                        <ArrowLeft className="h-3 w-3" /> Volver a fideicomisos
                    </Link>
                    <div className="flex items-start justify-between gap-6 flex-wrap">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-1">
                                {labelTipo(fid.tipo, TIPOS_FIDEICOMISO)} · {fid.numero_fiduciario}
                            </div>
                            <h1 className="font-display text-3xl lg:text-4xl tracking-tight">
                                {fid.numero_interno}
                            </h1>
                            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                                {fid.proposito}
                            </p>
                        </div>
                        <div className="flex flex-col items-end gap-2 text-right text-xs">
                            <span
                                className={
                                    est?.tone === "success" ? "badge-exito" : "badge-gold"
                                }
                            >
                                {est?.label || fid.estado_ciclo}
                            </span>
                            {completitud && (
                                <div className="text-muted-foreground">
                                    Completitud:{" "}
                                    <b
                                        className={
                                            completitud.porcentaje >= 90
                                                ? "text-[color:var(--brand-exito)]"
                                                : completitud.porcentaje >= 50
                                                  ? "text-[color:var(--brand-dark-gold)]"
                                                  : "text-[color:var(--brand-coral)]"
                                        }
                                    >
                                        {completitud.porcentaje}%
                                    </b>
                                </div>
                            )}
                            <div className="text-muted-foreground text-[11px]">
                                Constitución: {fid.fecha_constitucion} · Notario:{" "}
                                {fid.notario || "—"}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Tabs defaultValue="resumen" className="px-8 lg:px-10 pt-6">
                <TabsList className="mb-6">
                    <TabsTrigger value="resumen" data-testid="tab-resumen">
                        Resumen
                    </TabsTrigger>
                    <TabsTrigger value="partes" data-testid="tab-partes">
                        Partes y KYC
                    </TabsTrigger>
                    <TabsTrigger value="documental" data-testid="tab-documental">
                        Documental
                    </TabsTrigger>
                    <TabsTrigger value="bitacora" data-testid="tab-bitacora">
                        Bitácora
                    </TabsTrigger>
                    <TabsTrigger value="patrimonio" disabled data-testid="tab-patrimonio">
                        Patrimonio (Fase 2)
                    </TabsTrigger>
                    <TabsTrigger value="cuentas" disabled>
                        Cuentas (Fase 2)
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="resumen">
                    <TabResumen
                        fideicomiso={fid}
                        completitud={completitud}
                        onReload={() => {
                            loadFid();
                            loadCompletitud();
                        }}
                    />
                </TabsContent>
                <TabsContent value="partes">
                    <TabPartes fideicomisoId={id} onReload={loadCompletitud} />
                </TabsContent>
                <TabsContent value="documental">
                    <TabDocumental fideicomisoId={id} fideicomiso={fid} onReload={loadCompletitud} />
                </TabsContent>
                <TabsContent value="bitacora">
                    <TabBitacoraFid fideicomisoId={id} />
                </TabsContent>
            </Tabs>
        </div>
    );
}
